import React from 'react';
import { TagOutlined } from '@ant-design/icons';

export const ADD_LABEL_CHIP_BASE_CLASS =
	'footnoteregular box-border h-[30px] cursor-pointer items-center gap-1 rounded-full border border-solid !border-grey-5 bg-white px-2 py-1 text-grey-10 hover:bg-grey-2';

export const ADD_LABEL_CHIP_ICON_CLASS =
	'inline-flex h-3 w-3 shrink-0 items-center justify-center text-[12px] leading-none';

export const ADD_LABEL_CHIP_REVEAL_ON_HOVER_CLASS =
	'inline-flex pointer-events-none opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus:pointer-events-auto focus:opacity-100';

export const ADD_LABEL_CHIP_HOLLOW_ON_DARK_CLASS =
	'footnoteregular box-border h-[30px] cursor-pointer items-center gap-1 rounded-full border border-solid border-white bg-transparent px-2 py-[3px] text-white';

export type AddLabelChipVariant = 'default' | 'hollowOnDark';

interface Props {
	onClick: (event: React.MouseEvent) => void;
	/** Controls the chip's own display utility. Defaults to always-visible; pass a row's
	 * reveal-on-hover class (ADD_LABEL_CHIP_REVEAL_ON_HOVER_CLASS) to hide the chip until an
	 * ancestor `.group` is hovered — that constant keeps the trigger in the tab order, which
	 * `hidden`/`display: none` would not. */
	displayClassName?: string;
	/** `hollowOnDark`: white outline on a transparent background, for the detail-page heroes. */
	variant?: AddLabelChipVariant;
	disabled?: boolean;
}

/** Shared "+"-with-tag-icon pill for triggering "add labels" — used on work order rows, the WO detail header, and parts rows. */
const AddLabelChip: React.FC<Props> = ({
	onClick,
	displayClassName = 'inline-flex',
	variant = 'default',
	disabled = false,
}) => {
	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (disabled) return;
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick(event as unknown as React.MouseEvent);
		}
	};

	const isHollowOnDark = variant === 'hollowOnDark';

	return (
		<span
			role="button"
			tabIndex={disabled ? -1 : 0}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			className={`${displayClassName} ${
				isHollowOnDark ? ADD_LABEL_CHIP_HOLLOW_ON_DARK_CLASS : ADD_LABEL_CHIP_BASE_CLASS
			}${disabled ? ' pointer-events-none opacity-50' : ''}`}
		>
			<span>+</span>
			<TagOutlined
				size={12}
				className={`${ADD_LABEL_CHIP_ICON_CLASS}${isHollowOnDark ? ' !text-white' : ''}`}
			/>
		</span>
	);
};

export default AddLabelChip;
